<template>
  <v-app
    v-if="$auth.user"
  >
    <v-banner
      v-if="appEnv !== 'production'"
      max-width="490px"
      width="100%"
      max-height="75px"
      class="py-1"
      position="fixed"
      rounded
      data-html2canvas-ignore
      style="bottom: 0; right: 1px; z-index: 1; background: rgba(255, 0, 0, 0.4);color: #fff; pointer-events: none; user-select: none"
    >
      <div class="text-center">
        You are viewing a development version of this site (environment: {{ appEnv }})
      </div>
    </v-banner>
    <app-error-messages />
    <v-container
      ref="headerContainer"
      fluid
      class="header px-1 px-md-4"
      :class="{
        'settings-header': $route?.meta.navLayout === 'settings'
      }"
      :style="headerStyles"
    >
      <div
        class="cover-image"
        :style="{
          backgroundImage: $auth.user?.cover_picture_id ? `url(${$file.url($auth.user?.cover_picture_id, $file.imageSize.large)})` : ''
        }"
      />
      <v-row class="nav-row flex-nowrap">
        <v-col
          class="d-flex flex-nowrap ga-1 ga-md-4 align-center"
          :class="$vuetify.display.smAndDown || $route?.meta.navLayout === 'settings' ? 'flex-grow-0' : 'flex-grow-1'"
        >
          <v-menu
            v-model="profileMenuOpen"
            :close-on-content-click="false"
            offset="8"
            data-html2canvas-ignore
            content-class="nav-menu"
            @update:model-value="$event ? showAccountSwitch = false : undefined"
          >
            <template #activator="{ props }">
              <v-icon
                ref="menuArrow"
                class="menu-arrow mr-n4"
                size="40"
                v-bind="props"
                :style="menuArrowStyles"
              >
                arrow_drop_down
              </v-icon>
              <v-badge
                offset-y="-40"
              >
                <profile-image
                  ref="profileImage"
                  :user="$auth.user"
                  size="180"
                  class="cursor-pointer profile-image"
                  bordered
                  border-width="8"
                  v-bind="props"
                  :style="profileImageStyles"
                />
                <template #badge>
                  <StopImpersonatingButton
                    class="profile-image-stop-impersonating"
                    :style="impersonationBtnStyles"
                  />
                </template>
              </v-badge>
            </template>

            <v-card
              v-if="showAccountSwitch"
              class="pa-2 pa-md-4"
            >
              <account-switcher
                @back="showAccountSwitch = false"
                @success="profileMenuOpen = false"
              />
            </v-card>
            <v-list
              v-else
              class="text-uppercase"
            >
              <v-list-item
                @click="showAccountSwitch = true"
                v-if="$auth.user?.account.users_count > 1"
              >
                <v-icon class="mr-4">
                  swap_horiz
                </v-icon>
                Switch profile
              </v-list-item>

              <v-list-item @click="logout">
                <v-icon class="mr-4">
                  logout
                </v-icon>
                Log out
              </v-list-item>
            </v-list>
          </v-menu>

          <div
            v-if="$vuetify.display.smAndDown && !btnsExpanded"
            class="page-toggle-wrapper"
          >
            <v-btn-toggle
              class="page-toggle"
              color="primary"
              density="compact"
              mandatory
              :model-value="0"
              @click="btnsExpanded = true"
            >
              <v-btn>
                <v-icon
                  v-if="$route.name == ROUTES.CALENDAR || $route.name.startsWith('event')"
                  outlined
                >
                  calendar_month
                </v-icon>
                <v-icon
                  v-else-if="$route.name.startsWith('todos')"
                >
                  checklist
                </v-icon>
                <stylus-icon
                  v-else-if="$route.name === ROUTES.ANNOTATIONS"
                  color="white"
                />
                <gear-icon
                  v-else-if="$route.name === ROUTES.SETTINGS"
                  color="white"
                />
                <v-icon size="20">
                  chevron_right
                </v-icon>
              </v-btn>
            </v-btn-toggle>
          </div>

          <div
            v-else
            class="page-toggle-wrapper"
          >
            <v-btn-toggle
              class="page-toggle"
              color="primary"
              density="compact"
              mandatory
              @click="btnsExpanded = false"
            >
              <v-btn
                value="calendar"
                :to="$routeTo(ROUTES.CALENDAR)"
                :class="{
                  active: $route.name === ROUTES.CALENDAR || $route.name.startsWith('event')
                }"
                v-tooltip:bottom-end="'Calendar'"
              >
                <v-icon
                  :color="$route.name === ROUTES.CALENDAR || $route.name.startsWith('event') ? undefined : 'primary'"
                  outlined
                >
                  calendar_month
                </v-icon>
                <div
                  class="btn-text"
                  :class="{
                    shown: $vuetify.display.smAndUp && $route.name === ROUTES.CALENDAR || $route.name.startsWith('event'),
                    'text-secondary': !( $route.name === ROUTES.CALENDAR || $route.name.startsWith('event') ),
                  }"
                >
                  Calendar
                </div>
              </v-btn>
              <v-btn
                value="lists"
                :to="$routeTo(ROUTES.TODOS)"
                :class="{
                  active: $route.name.startsWith(ROUTES.TODOS)
                }"
                v-tooltip:bottom-end="'Goals'"
              >
                <v-icon
                  :color="$route.name.startsWith(ROUTES.TODOS) ? undefined : 'primary'"
                >
                  checklist
                </v-icon>
                <div
                  class="btn-text"
                  :class="{
                    shown: $vuetify.display.smAndUp && $route.name.startsWith(ROUTES.TODOS),
                    'text-primary': !$route.name.startsWith(ROUTES.TODOS),
                  }"
                >
                  Goals
                </div>
              </v-btn>
              <v-btn
                value="annotations"
                :to="$routeTo(ROUTES.ANNOTATIONS)"
                v-tooltip:bottom-end="'Notes'"
              >
                <stylus-icon
                  :color="$route.name === ROUTES.ANNOTATIONS ? 'white' : '#5665fb'"
                />
                <div
                  class="btn-text"
                  :class="{
                    shown: $vuetify.display.smAndUp && $route.name === ROUTES.ANNOTATIONS,
                    'text-primary': $route.name !== ROUTES.ANNOTATIONS,
                  }"
                >
                  Notes
                </div>
              </v-btn>
              <v-btn
                value="settings"
                :to="$routeTo(ROUTES.SETTINGS)"
                v-tooltip:bottom-end="'Settings'"
              >
                <gear-icon
                  :color="$route.name === ROUTES.SETTINGS ? 'white' : '#5665fb'"
                />
                <div
                  class="btn-text"
                  :class="{
                    shown: $vuetify.display.smAndUp && $route.name === ROUTES.SETTINGS,
                    'text-primary': $route.name !== ROUTES.SETTINGS,
                  }"
                >
                  Settings
                </div>
              </v-btn>
            </v-btn-toggle>
          </div>
          <StopImpersonatingButton
            class="profile-image-stop-impersonating-inline"
            :style="impersonationInlineStyles"
          />
        </v-col>
        <template v-if="$vuetify.display.smAndUp || !btnsExpanded">
          <template v-if="$route?.meta.navLayout !== 'settings'">
            <v-col
              class="center-col justify-end"
            >
              <v-spacer class="d-none d-md-block" />
              <h2
                class="d-flex align-center text-no-wrap cursor-pointer user-select-none"
              >
                <template v-if="[ROUTES.CALENDAR, ROUTES.CALENDAR_ANNOTATIONS_ADD, ROUTES.ANNOTATIONS_ADD].includes($route?.name)">
                  <div
                    class="flex-shrink-1 page-title"
                    id="date-range-title"
                    :class="{
                      'line-clamp-1': $vuetify.display.smAndDown,
                    }"
                    @click.stop="showCalendarSelect = !showCalendarSelect"
                  >
                    <span class="d-none d-md-inline">{{ calendarTitle }}</span>
                    <span class="d-md-none">{{ truncatedCalendarTitle }}</span>
                  </div>
                  <v-btn
                    icon
                    flat
                    class="ml-2"
                    @click.stop="showCalendarSelect = !showCalendarSelect"
                  >
                    <v-icon
                      size="40"
                    >
                      arrow_drop_down
                    </v-icon>
                  </v-btn>
                </template>
                <template v-else>
                  <v-btn
                    v-if="pageTitle"
                    flat
                    icon="arrow_back"
                    class="opacity-60"
                    :to="backOrCalendar"
                  />
                  <div
                    class="flex-shrink-1 page-title"
                    :class="{
                      'line-clamp-1': $vuetify.display.smAndDown,
                    }"
                  >
                    {{ pageTitle }}
                  </div>
                </template>
              </h2>
              <v-spacer class="d-none d-md-block" />
            </v-col>
            <v-col class="d-none d-md-block flex-grow-1 flex-shrink-0" />
          </template>
          <template v-else>
            <v-col class="flex-grow-1 d-flex flex-nowrap align-center pr-8 pr-md-16 justify-end justify-md-start">
              <v-btn
                flat
                icon="arrow_back"
                :to="backOrCalendar"
              />
              <h3>Settings</h3>
              <v-divider
                vertical
                class="mx-8 my-4 d-none d-xl-block"
              />
              <SettingsTabs
                class="d-none d-xl-block"
              />
            </v-col>
          </template>
        </template>
      </v-row>
    </v-container>
    <v-main
      class="position-relative"
      :style="{paddingBottom: appEnv !== 'production' ? '54px' : null}"
    >
      <slot />
    </v-main>
  </v-app>
</template>

<script lang="ts">
import {defineComponent} from "vue"
import AppErrorMessages from "@/components/AppErrorMessages.vue"
import ProfileImage from "@/components/ProfileImage.vue"
import {capitalize, trim} from "lodash"
import {mapActions, mapWritableState} from "pinia"
import AccountSwitcher from "@/components/AccountSwitcher.vue"
import SettingsTabs from "@/components/SettingsTabs.vue"
import StopImpersonatingButton from "@/components/StopImpersonatingButton.vue"
import StylusIcon from "@/components/Icons/StylusIcon.vue"
import GearIcon from "@/components/Icons/GearIcon.vue"
import months from "@/data/months"
import truncate from "../utils/truncate"
import {useCalendarStore} from "@/stores/calendar"
import {CalendarViews} from "@/enums/calendarViews"
import moment from "@/utils/moment"

export default defineComponent({
  components: {
    GearIcon,
    StylusIcon,
    StopImpersonatingButton,
    SettingsTabs,
    AccountSwitcher,
    ProfileImage,
    AppErrorMessages
  },
  data() {
    return {
      btnsExpanded: false,
      profileMenuOpen: false,
      showAccountSwitch: false,
      loading: '',
      scrollProgress: 0, // value between 0 and 1 based on scroll position
      scrollThreshold: 100,
    }
  },
  mounted() {
    this.updateScrollProgress()
    window.addEventListener("scroll", this.handleScroll)

    const currentUrl = window.location.href
    const newUrl = currentUrl.split('?')[0]

    //Case when coming from the daily digest link
    const userId = parseInt(this.$route.query['id']) || null
    const date = this.$route.query['date'] || null
    if (userId && userId !== this.$auth.user?.id) {
      this.profileMenuOpen = true
      this.showAccountSwitch = true
    }
    if(date) {
      const start = moment(date)
      this.setDate(CalendarViews.DAY, start)
    }

    if(userId || date) {
      history.replaceState({}, '', newUrl)
    }
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll)
  },
  computed: {
    ...mapWritableState(useCalendarStore, ["showCalendarSelect", 'calendarStart', 'calendarView']),
    calendarTitle() {
      // Required to trigger update on start_of_week change
      this.$auth.user?.settings?.start_of_week

      switch (this.calendarView) {
        case CalendarViews.YEAR:
          return moment(this.calendarStart).format(this.$vuetify.date.formats.humanYear)
        case CalendarViews.MONTH:
          return moment(this.calendarStart).format(this.$vuetify.date.formats.humanMonth)
        case CalendarViews.WEEK:
          if (this.$vuetify.display.smAndUp) {
            return `${moment(this.calendarStart).startOf('week').format('MMM D')} - ${moment(this.calendarStart).endOf('week').format('MMM D')}, ${moment(this.calendarStart).format('YYYY')}`
          } else {
            return `${moment(this.calendarStart).format('MMM D')} - ${moment(this.calendarStart).add(1, 'day').format('MMM D')}, ${moment(this.calendarStart).format('YYYY')}`
          }
        default:
          return moment(this.calendarStart).format(this.$vuetify.date.formats.humanDate)
      }
    },
    appEnv() {
      return import.meta.env.VITE_APP_ENV
    },
    pageTitle() {
      if (this.$route?.meta?.title === false) return ''
      if (this.$vuetify.display.smAndDown && this.$route?.meta?.shortTitle) {
        return this.$route?.meta?.shortTitle
      }
      return this.$route?.meta?.title || capitalize(this.$route?.name as string).replace('-', ' ')
    },
    truncatedCalendarTitle() {
      let title = this.calendarTitle

      // replace every month with its first 3 letters
      months.forEach(month => {
        title = title.replace(month.name, month.abbr)
      })

      // if last 4 characters of title are numbers (representing year), remove them and trim
      // unless the title is just the year, or just the month and year
      if (title.length > 8 && title.slice(-4).match(/^\d+$/)) {
        title = trim(title.slice(0, -4).trim(), ',')
      }

      return title
    },
    backOrCalendar() {
      if (this.$route.name === this.ROUTES.TODOS_EDIT) {
        return this.$routeTo(this.ROUTES.TODOS)
      }
      return this.$routeTo(this.ROUTES.CALENDAR)
    },
    userTitleFont() {
      return this.$auth.user?.settings?.title_font || 'Outfit'
    },
    userParagraphFont() {
      return this.$auth.user?.settings?.paragraph_font || 'Filson Pro'
    },
    fontSizeAdjust() {
      return this.$auth.user?.settings?.font_size == 'Large' ? 0.56 : this.$auth.user?.settings?.font_size == 'Medium' ? 0.53 : 'unset'
    },
    primaryFontColor() {
      return this.$auth.user?.settings?.primary_font_color == '#111110' ? 'unset' : this.$auth.user?.settings?.primary_font_color || 'inherit'
    },
    secondaryFontColor() {
      return this.$auth.user?.settings?.secondary_font_color == '#727272' ? 'unset' : this.$auth.user?.settings?.secondary_font_color || 'inherit'
    },
    // Styles computed from scrollProgress:
    headerStyles() {
      // header moves from top: 0 to top: -200px and box shadow appears gradually.
      const top = 0 - 200 * this.scrollProgress // from 0px to -200px
      const boxShadow =
        this.scrollProgress > 0
          ? `4px 4px 19px 0 rgba(0, 0, 0, ${0.13 * this.scrollProgress})`
          : "none"
      return {
        top: `${top}px`,
        // transition: "top 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
        boxShadow,
      }
    },
    profileImageStyles() {
      // profile image: scale from 1 to 0.36, margin-left/right from 0 to -50px, top from -28px to 0.
      const scale = 1 - 0.64 * this.scrollProgress // 1 -> 0.36
      const marginLR = -50 * this.scrollProgress
      const top = -28 + 28 * this.scrollProgress // -28 -> 0
      return {
        transform: `scale(${scale})`,
        marginLeft: `${marginLR}px`,
        marginRight: `${marginLR}px`,
        top: `${top}px`,
        // transition: "transform 0.3s ease-out, margin 0.3s ease-out, top 0.3s ease-out",
      }
    },
    menuArrowStyles() {
      const top = 40 - 40 * this.scrollProgress
      const left = 20 - 20 * this.scrollProgress
      return {
        top: `${top}px`,
        left: `${left}px`,
        // transition: "top 0.3s ease-out, left 0.3s ease-out",
      }
    },
    impersonationBtnStyles() {
      const opacity = 1 - (this.scrollProgress > 0 ? 1 : 0)
      return {
        opacity,
        // transition: "opacity 0.1s ease-out",
      }
    },
    impersonationInlineStyles() {
      const opacity = this.scrollProgress > 0 ? 1 : 0
      return {
        opacity,
        // transition: "opacity 0.3s ease-out",
      }
    },
  },
  watch: {
    '$vuetify.display.smAndDown': {
      immediate: true,
      handler(val) {
        this.btnsExpanded = !val
      }
    }
  },
  methods: {
    ...mapActions(useCalendarStore, ['setDate']),
    truncate,
    capitalize,
    async logout() {
      await this.$auth.logout()
      await this.$router.push(this.$routeTo(this.ROUTES.LOGIN))
    },
    updateScrollProgress() {
      // Compute progress based on window.scrollY.
      // Here, progress is clamped between 0 and 1.

      //if we have v-overlay--active, we don't want to show the header
      if (document.querySelector('.v-overlay--active')) {
        this.scrollProgress = 1
        return
      }

      this.scrollProgress = Math.min(1, window.scrollY / this.scrollThreshold)
    },
    handleScroll() {
      this.updateScrollProgress()
    },
  },
})
</script>

<style lang="scss">
// global styles from user settings
h1, h2, h3, h4, h5, h6 {
  font-family: v-bind(userTitleFont);
  color: v-bind(primaryFontColor);
}

div, span {
  &:not(.v-btn &, h1 &, h2 &, h3 &, h4 &, h5 &, h6 &) {
    font-family: v-bind(userParagraphFont);
    font-size-adjust: v-bind(fontSizeAdjust);
    color: v-bind(primaryFontColor);
  }
}

#app {
  .text-secondary {
    color: v-bind(secondaryFontColor) !important;
  }
}
</style>


<style scoped lang="scss">
$collapseHeight: 700px;

@media #{map-get(settings.$display-breakpoints, 'sm-and-down')}, (max-height: $collapseHeight) {
  .v-main {
    margin-top: 80px;
  }
}

:deep() .nav-menu {
  transform: translateX(-50px);

  @media #{map-get(settings.$display-breakpoints, 'sm-and-down')} {
    transform: translateX(-30px);
  }
}


.header {
  position: sticky;
  padding-top: var(--v-safe-area-top);
  padding-bottom: 0;
  z-index: 100;
  background-color: white;
  top: -200px;


  @media #{map-get(settings.$display-breakpoints, 'sm-and-down')}, (max-height: $collapseHeight) {
    top: 0px !important;
    position: fixed;
  }

  &.settings-header {
    @media #{map-get(settings.$display-breakpoints, 'lg-and-down')} {
      box-shadow: 0 0 0 0 rgba(0, 0, 0, 0) !important;
    }
  }
}

.profile-image {
  margin-top: -50px;
  margin-bottom: -50px;
  top: -28px;

  position: relative;

  @media #{map-get(settings.$display-breakpoints, 'sm-and-down')}, (max-height: $collapseHeight) {
    transform: scale(0.36) !important;
    margin-left: -50px !important;
    margin-right: -50px !important;
    top: 0px !important;
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0) !important;
  }
}

:deep(.profile-image-wrapper + .v-badge__badge) {
  background: transparent;
}
.profile-image-stop-impersonating {
  @media #{map-get(settings.$display-breakpoints, 'sm-and-down')}, (max-height: $collapseHeight) {
    opacity: 0 !important;
  }
}

.profile-image-stop-impersonating-inline {
  opacity: 0;

  @media #{map-get(settings.$display-breakpoints, 'sm-and-down')}, (max-height: $collapseHeight) {
    opacity: 1 !important;
  }
}

.menu-arrow {
  position: relative;
  top: 40px;
  left: 20px;

  @media #{map-get(settings.$display-breakpoints, 'sm-and-down')}, (max-height: $collapseHeight) {
    top: 0px !important;
    left: 0px !important;
  }
}

.cover-image {
  background-size: cover;
  background-position: center;
  background-color: grey;
  height: var(--cover-image-height, 200px);
  width: 100%;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;

  @media #{map-get(settings.$display-breakpoints, 'sm-and-down')}, (max-height: $collapseHeight) {
    display: none;
  }
}

.nav-row {
  height: 104px;
}

.page-toggle:deep() {
  border: 1px solid rgb(var(--v-theme-primary));
  border-radius: 999px;

  .v-btn {
    min-width: unset;
    padding: 0 7px;
    transition: none;

    &.active {
      background-color: rgb(var(--v-theme-primary));
      color: white;
    }
  }
  .v-btn__content {
    font-size: 20px;

    .btn-text {
      font-size: 14px;
      max-width: 0px;
      margin-left: 0;
      overflow: hidden;

      transition: max-width 0.3s, margin-left 0.3s;

      &.shown {
        max-width: 100px;
        margin-left: 5px;
      }
    }
  }
}

.center-col {
  display: flex;
}

.page-title {
  @media #{map-get(settings.$display-breakpoints, 'sm-and-down')}, (max-height: $collapseHeight) {
    font-size: 25px;
    padding-right: 10px;
  }
}
</style>
